import {
  Box,
  Card,
  Column,
  Columns,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';
import type { FunctionComponent } from 'react';

import { defaultRoutes, useRoutes } from '../routes';

import { TextLinkComponent } from './TextLinkComponent';

interface SubscriptionProps {
  title: string;
  body: string | React.JSX.Element | React.ReactNode;
  cardTitle?: string;
  body2?: string | React.JSX.Element;
  icon?: string;
  svg?: React.ReactNode;
  link?: string;
  linkMessage?: string;
  linkMessage2?: string;
}

const Subscription: FunctionComponent<SubscriptionProps> = ({
  title,
  cardTitle,
  body,
  body2,
  icon,
  svg,
  link,
  linkMessage,
  linkMessage2,
}) => {
  const { preferenceCentrePath } = useRoutes();
  const internalRoutes: string[] = Object.values(defaultRoutes);
  const isInternalRoute = internalRoutes.includes(link || preferenceCentrePath);
  const textId = 'manage-link';
  const linkPath = link || preferenceCentrePath;

  const body2Component = body2 ? <Text>{body2}</Text> : null;
  const linkMessageComponent = linkMessage ? (
    <Text>
      <TextLinkComponent
        isInternalRoute={isInternalRoute}
        textId={textId}
        linkPath={linkPath}
        linkMessage={linkMessage}
      />
    </Text>
  ) : null;

  const linkMessage2Component = linkMessage2 ? (
    <Text>
      <TextLinkComponent
        isInternalRoute={isInternalRoute}
        textId="manage-link2"
        linkPath={preferenceCentrePath}
        linkMessage={linkMessage2}
      />
    </Text>
  ) : null;

  const cardTitleComponent = cardTitle ? (
    <Heading level="3">{cardTitle}</Heading>
  ) : null;

  return (
    <Stack space="gutter">
      <Box
        data-testid="subscription-title"
        paddingTop="gutter"
        paddingLeft={['gutter', 'gutter', 'none']}
      >
        <Heading level="2" component="h1">
          {title}
        </Heading>
      </Box>
      <Card>
        <Columns space="gutter" alignY="center" collapseBelow="tablet">
          <Column width="content">
            <Box textAlign="center" paddingX="small">
              {svg ? (
                <span role="img" aria-label="subscription-icon">
                  {svg}
                </span>
              ) : (
                <img
                  alt="subscription-icon"
                  style={{
                    width: '88px',
                  }}
                  src={icon}
                />
              )}
            </Box>
          </Column>
          <Column>
            <div data-testid="subscription-body">
              <Stack space="medium">
                {cardTitleComponent}
                <Text>{body}</Text>
                {body2Component}
                {linkMessageComponent}
                {linkMessage2Component}
              </Stack>
            </div>
          </Column>
        </Columns>
      </Card>
    </Stack>
  );
};

export default Subscription;
